import L from 'leaflet';
import { blockToLatLng } from './coords';
import type { Biome, Country, GeoRegion, Marker } from '../worlds.types';
import type { CountryCentroid } from './chunkGrid';

// ============================================================
// Surcouches DOM de la carte : centres des pays, marqueurs de biomes, libellés de régions
// ============================================================
// Quelques centaines d'éléments au plus : des L.marker à divIcon suffisent, sans canvas.
// Les classes CSS viennent du module SCSS de la page (passées par l'appelant).
// ============================================================

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

// ---- Centres des pays ----

export interface CenterHandlers {
  onSelect: (c: Country) => void;
  /** Déplacement terminé : x/z en blocs. Renvoie false pour remettre le point à sa place. */
  onMove: (c: Country, x: number, z: number) => Promise<boolean>;
}

export function centerIcon(className: string, color: string, selected: boolean): L.DivIcon {
  return L.divIcon({
    className: `${className}${selected ? ' is-selected' : ''}`,
    html: `<span style="background:${escapeHtml(color)}"></span>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
}

/**
 * (Re)construit les points centres. Un pays sans centre n'a pas de point. `draggable` n'est vrai
 * qu'en écriture et en mode Déplacer.
 */
export function buildCenters(
  group: L.LayerGroup, countries: Country[], opts: {
    className: string; selectedId: string | null; draggable: boolean; handlers: CenterHandlers;
  },
): void {
  group.clearLayers();
  for (const c of countries) {
    if (c.centerX == null || c.centerZ == null) continue;
    const home = blockToLatLng(c.centerX + 0.5, c.centerZ + 0.5);
    const m = L.marker(home, {
      icon: centerIcon(opts.className, c.color, c.id === opts.selectedId),
      draggable: opts.draggable,
      keyboard: false,
      zIndexOffset: c.id === opts.selectedId ? 1000 : 0,
    });
    m.bindTooltip(`${escapeHtml(c.slug)} (${c.chunkCount.toLocaleString('fr-FR')} chunks)`, { direction: 'top', offset: [0, -6] });
    m.on('click', () => opts.handlers.onSelect(c));
    m.on('dragend', async () => {
      const ll = m.getLatLng();
      const ok = await opts.handlers.onMove(c, Math.floor(ll.lng * 16), Math.floor(-ll.lat * 16));
      if (!ok) m.setLatLng(home);
    });
    group.addLayer(m);
  }
}

// ---- Marqueurs de biomes ----

export interface MarkerHandlers {
  onSelect: (m: Marker) => void;
  onMove: (m: Marker, x: number, z: number) => Promise<boolean>;
}

export function biomeIcon(className: string, biome: Biome | undefined, selected: boolean): L.DivIcon {
  const color = biome?.color ?? '#9CA3AF';
  // L'icône est optionnelle : si l'image ne charge pas, seule la pastille reste (onerror la masque).
  const img = biome?.iconUrl
    ? `<img src="${escapeHtml(biome.iconUrl)}" alt="" onerror="this.style.display='none'" />`
    : '';
  return L.divIcon({
    className: `${className}${selected ? ' is-selected' : ''}${!biome || !biome.enabled ? ' is-orphan' : ''}`,
    html: `<span style="background:${escapeHtml(color)}">${img}</span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

export function buildMarkers(
  group: L.LayerGroup, markers: Marker[], biomes: Map<string, Biome>, opts: {
    className: string; selectedId: string | null; draggable: boolean; handlers: MarkerHandlers;
  },
): void {
  group.clearLayers();
  for (const mk of markers) {
    const biome = biomes.get(mk.biomeName);
    const pos = blockToLatLng(mk.x + 0.5, mk.z + 0.5);
    const m = L.marker(pos, {
      icon: biomeIcon(opts.className, biome, mk.id === opts.selectedId),
      draggable: opts.draggable,
      keyboard: false,
      zIndexOffset: mk.id === opts.selectedId ? 1000 : 0,
    });
    // Biome désactivé : le marqueur reste visible (atténué), l'infobulle le dit.
    const label = biome
      ? `${biome.displayNameFr || biome.name}${biome.enabled ? '' : ' (désactivé)'}`
      : `${mk.biomeName} (biome absent)`;
    m.bindTooltip(`${escapeHtml(label)} · ${mk.x}, ${mk.z}`, { direction: 'top', offset: [0, -14] });
    m.on('click', () => opts.handlers.onSelect(mk));
    m.on('dragend', async () => {
      const ll = m.getLatLng();
      const ok = await opts.handlers.onMove(mk, Math.floor(ll.lng * 16), Math.floor(-ll.lat * 16));
      if (!ok) m.setLatLng(pos);
    });
    group.addLayer(m);
  }
}

// ---- Libellés des géo-régions ----

export interface RegionLabel {
  region: GeoRegion;
  /** Emprise (chunks) : seuil d'affichage et taille du texte. */
  bbox: { minX: number; maxX: number; minZ: number; maxZ: number };
  /** Point du libellé, en chunks (centroïde des chunks de la région, sinon centre de l'emprise). */
  at: { x: number; z: number };
}

/**
 * Libellé d'une région, comme RegionContoursLayer de NG : au centroïde de ses chunks sur le niveau
 * courant (pondéré par le nombre de chunks de chaque pays), emprise = union de celles de ses pays.
 * `centroids` vient de la grille locale (ChunkGrid.centroids, toujours à jour) ; sans elle, ou pour
 * un pays absent de la grille, on se rabat sur la bbox calculée par le satellite (centre de
 * l'emprise).
 */
export function regionLabels(
  regions: GeoRegion[], countries: Country[], centroids?: Map<number, CountryCentroid> | null,
): RegionLabel[] {
  const byRegion = new Map<string, Country[]>();
  for (const c of countries) {
    if (!c.geoRegionId) continue;
    const list = byRegion.get(c.geoRegionId);
    if (list) list.push(c); else byRegion.set(c.geoRegionId, [c]);
  }
  const out: RegionLabel[] = [];
  for (const r of regions) {
    const list = byRegion.get(r.id);
    if (!list) continue;
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    let sx = 0, sz = 0, n = 0;
    for (const c of list) {
      const g = centroids?.get(c.idx);
      const b = g ?? (centroids ? null : c.bbox);
      if (!b) continue;
      minX = Math.min(minX, b.minX);
      maxX = Math.max(maxX, b.maxX);
      minZ = Math.min(minZ, b.minZ);
      maxZ = Math.max(maxZ, b.maxZ);
      if (g) { sx += g.sx; sz += g.sz; n += g.n; }
    }
    if (minX === Infinity) continue;
    const at = n > 0
      ? { x: sx / n, z: sz / n }
      : { x: (minX + maxX + 1) / 2, z: (minZ + maxZ + 1) / 2 };
    out.push({ region: r, bbox: { minX, maxX, minZ, maxZ }, at });
  }
  return out;
}

/** Taille du texte d'un libellé : 1/8 du plus petit côté de l'emprise à l'écran, entre 12 et 26 px. */
export function regionLabelSize(bbox: RegionLabel['bbox'], zoom: number): number | null {
  const px = Math.pow(2, zoom);
  const w = (bbox.maxX - bbox.minX + 1) * px;
  const h = (bbox.maxZ - bbox.minZ + 1) * px;
  // Emprise de moins de 50 px sur un côté : texte illisible, libellé masqué (même seuil que NG).
  if (Math.min(w, h) < 50) return null;
  return Math.max(12, Math.min(26, Math.round(Math.min(w, h) / 8)));
}

/**
 * (Re)construit les libellés : texte blanc cerclé de noir (classe `className`), pastille de la
 * couleur de la région en tête (classe `dotClass`), recalculés à chaque zoom.
 */
export function buildRegionLabels(
  group: L.LayerGroup, labels: RegionLabel[], className: string, zoom: number, dotClass?: string,
): void {
  group.clearLayers();
  for (const { region, bbox, at } of labels) {
    const size = regionLabelSize(bbox, zoom);
    if (size == null) continue;
    const dot = dotClass ? `<i class="${escapeHtml(dotClass)}" style="background:${escapeHtml(region.color)}"></i>` : '';
    group.addLayer(L.marker(L.latLng(-at.z, at.x), {
      interactive: false,
      keyboard: false,
      icon: L.divIcon({
        className,
        html: `<span style="font-size:${size}px">${dot}${escapeHtml(region.name)}</span>`,
        iconSize: [0, 0],
      }),
    }));
  }
}
